import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/use-auth";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MainLogo } from "modules/main/assets/main_logo";

interface ConditionalProtectedRouteProps {
    children: JSX.Element;
    requireAuth: boolean;
    requirePremium?: boolean;
}

export const ConditionalProtectedRoute = ({ children, requireAuth, requirePremium = false }: ConditionalProtectedRouteProps) => {
    const { isLogged, isPremiumUser, checkAuth } = useAuth();
    const [authChecked, setAuthChecked] = useState<boolean | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const location = useLocation();
    const hasShownPremiumToastRef = useRef(false);

    useEffect(() => {
        let isActive = true;
        const loggedOnStart: boolean = isLogged;

        if (requireAuth) {
            if (isLogged) {
                setIsAuthenticated(true);
                setAuthChecked(true);
            } else {
                const verifyAuth = async () => {
                    const authenticated = await checkAuth();
                    if (!isActive) {
                        return;
                    }
                    // Si el usuario se logueó durante el check async (login Google reciente),
                    // ignorar el resultado stale y dejarlo pasar.
                    if (!loggedOnStart && isLogged) {
                        setIsAuthenticated(true);
                        setAuthChecked(true);
                        return;
                    }
                    setIsAuthenticated(authenticated);
                    setAuthChecked(true);
                };
                verifyAuth();
            }
        } else {
            setAuthChecked(true);
            setIsAuthenticated(isLogged);
        }

        return () => {
            isActive = false;
        };
    }, [requireAuth, checkAuth, isLogged]);

    // Mostrar loading solo para rutas que requieren auth
    if (requireAuth && authChecked === null) {
        return null; // o un spinner de carga
    }

    // Para rutas que requieren auth, verificar autenticación
    if (requireAuth && !isAuthenticated && !isLogged) {
        return <Navigate to="/login" state={{ from: location }} />;

    }

    if (requirePremium && !isPremiumUser) {
        if (!hasShownPremiumToastRef.current) {
            toast.custom(() => (
                <div className="w-full rounded-lg border bg-popover px-4 py-3 text-popover-foreground shadow-lg">
                    <div className="flex flex-col gap-2">
                        <div className="flex flex-row items-end gap-2">
                            <MainLogo classname="h-5 w-5" />
                            <span className="font-extrabold">Acceso exclusivo para Carriapp Plus</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Esta función esta disponible solo para usuarios premium. <span className="font-bold text-popover-foreground">Actualiza tu plan para crear carritos aqui.</span>
                        </p>
                    </div>
                </div>
            ));
            hasShownPremiumToastRef.current = true;
        }
        // console.log("Redirigiendo por falta de acceso premium");
        return <Navigate to="/login" state={{ from: location }} />;
    }

    hasShownPremiumToastRef.current = false;

    return children;
};
